<script lang="ts">
  import { checkExists, login } from "$lib/store/auth-context";
  import { isLoading } from "$lib/store/loading-context";
  import { makeDebounce } from "$lib/utils";
  import { Spinner } from "@thehood/svlib";

  export let step: number;
  export let atCheckout = false;
  export let lastInput = "";
  export let error: string = "";

  let formRef: HTMLFormElement;

  const checkedList = new Map<string, boolean[]>();
  function showSasswordLogin(ev: Event) {
    ev.stopImmediatePropagation();
    if (!formRef) {
      return;
    }
    const { value: email } = getEmailValue();
    lastInput = email;
    step = 4;
  }

  async function handleSubmit(ev: Event) {
    ev.stopImmediatePropagation();
    ev.preventDefault();
    isLoading.set(true);
    const { value: email } = getEmailValue();
    lastInput = email;
    login(email, atCheckout, formRef.action as string)
      .then(({ userExist, message }) => {
        error = message;
        step = userExist ? 3 : 2;
      })
      .catch(e => console.log(e))
      .finally(() => isLoading.set(false));
  }

  const debounceCheck = makeDebounce(async () => {
    if (!formRef) {
      return;
    }
    userExist = false;
    canUsePassword = false;
    const { value } = getEmailValue();
    if (!isValidEmail) {
      return;
    }

    let results = checkedList.get(value);
    if (!results) {
      const { userExist, passwordLogin } = await checkExists(value);
      checkedList.set(value, [userExist, passwordLogin]);
      results = [userExist, passwordLogin];
    }

    userExist = results[0];
    canUsePassword = results[1];
  }, 100);

  let isValidEmail = false;
  let userExist = false;
  let canUsePassword = false;
  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  function checkEmail(formEl = formRef) {
    if (!formEl) {
      return;
    }
    const { value, inputEl } = getEmailValue();
    isValidEmail = inputEl.checkValidity() && emailRegex.test(`${value}`.trim());
    if (isValidEmail) {
      debounceCheck();
    }
  }

  function getEmailValue() {
    const inputEl = formRef.querySelector("#email-field") as HTMLInputElement;
    const { value } = inputEl;
    return { value, inputEl };
  }

  function keyUp(ev: KeyboardEvent) {
    ev.stopImmediatePropagation();
    checkEmail();
    if (!isValidEmail) {
      return;
    }
  }

  function keyDown(ev: KeyboardEvent) {
    ev.stopImmediatePropagation();
    checkEmail();
    if (!isValidEmail) {
      return;
    }

    const { key } = ev;
    if (key === "Enter") {
      ev.preventDefault();
      handleSubmit(ev);
    }
  }

  $: checkEmail(formRef);
</script>

{#if $isLoading}
  <div class="loading grid">
    <Spinner />
  </div>
{:else}
  <form bind:this={formRef} action="/z/auth/login" method="GET" on:submit={handleSubmit}>
    <h1 class="sf-700"><slot />Create an account or sign in</h1>
    <hr />
    <p class="sf-300">
      Footballticketpad uses a password-less system. Enter your email and we’ll send you a verification link to sign up
      or sign in.
    </p>
    <div class="input-wrap" role="button" on:keyup={keyUp} on:keydown={keyDown} tabindex="0">
      <input
        type="email"
        class="form-control mb-3"
        id="email-field"
        name="email"
        placeholder="Email"
        value={lastInput}
        on:change={() => checkEmail(formRef)}
        required
      />
      <div class="cta" class:two-btn={canUsePassword}>
        <button class="btn btn-primary pf-500" type="button" disabled={!isValidEmail} on:click={handleSubmit}>
          {#if !isValidEmail}
            <span>Continue</span>
          {:else if userExist}
            <span>Email Login</span>
          {:else}
            <span>Create Account</span>
          {/if}
        </button>

        <button
          class="btn btn-primary pf-500 password-login-btn"
          class:show={canUsePassword}
          type="button"
          disabled={!isValidEmail}
          on:click={showSasswordLogin}
        >
          Password Login
        </button>
      </div>
    </div>
    <hr />
  </form>
{/if}

<style lang="scss">
  @use "@styles/breakpoint-mixins" as *;
  .loading {
    position: relative;
    place-items: center;
  }

  form {
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    hr,
    input,
    button {
      width: 100%;
    }

    h1 {
      align-self: flex-start;
      font-size: 1.25em;
      margin: 0;
      line-height: 1.5em;
    }

    hr {
      margin-top: calc(1em / 0.875 * 1.25);
      border-top: 1px solid var(--hr-color);
    }

    p {
      font-size: 1em;
      margin: calc(1em / 0.875 * 1.25);
      line-height: 1.5;
    }

    .input-wrap {
      width: 100%;
      input {
        padding: 0.63em 1.43em 0.63em 1.75em;
        background: #fff;
        border-radius: 0.3125em;
        border: 1px solid #404469;
        height: 3.125em;
        margin-bottom: calc(1em / 0.875 * 1.25);
      }

      button[type="button"] {
        border-radius: 0.3125em;
        line-height: 1.875em;
        height: 4.08956em;
        @media (min-width: 400px) {
          height: 3.08956em;
        }
        @include desktop {
          height: 3.125em;
        }

        &.password-login-btn {
          visibility: hidden;
          width: 0;
          height: 0;
          padding: 0;
          transition: width 0.3s ease-in-out;
          overflow: hidden;

          &.show {
            visibility: visible;
            width: 100%;
            height: 4.08956em;
            @media (min-width: 400px) {
              height: 3.08956em;
            }
            @include desktop {
              min-height: 3.125em;
            }
          }
        }
      }
    }
  }

  .cta {
    display: flex;
    &.two-btn {
      gap: 10px;
    }
  }
</style>
